import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  Download, Search, Filter, Eye, Check, X, Image, ExternalLink,
  Loader2, Clock, CheckCircle, XCircle, DollarSign, Wallet, Bitcoin
} from 'lucide-react';

export default function AdminDeposits() {
  const [deposits, setDeposits] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [cryptoFilter, setCryptoFilter] = useState('all');
  
  // Modals
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [proofModalOpen, setProofModalOpen] = useState(false);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchDeposits = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (cryptoFilter !== 'all') params.crypto_type = cryptoFilter;
      
      const response = await adminAPI.getDeposits(params);
      setDeposits(response.data.deposits || []);
      setStats(response.data.stats || {});
    } catch (error) {
      toast.error('Failed to fetch deposits');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeposits();
  }, [statusFilter, cryptoFilter]);

  const handleViewDetails = (deposit) => {
    setSelectedDeposit(deposit);
    setViewModalOpen(true);
  };

  const handleViewProof = (deposit) => {
    setSelectedDeposit(deposit);
    setProofModalOpen(true);
  };

  const handleAction = (deposit, type) => {
    setSelectedDeposit(deposit);
    setActionType(type);
    setAdminNotes('');
    setActionModalOpen(true);
  };

  const executeAction = async () => {
    try {
      setActionLoading(true);
      const status = actionType === 'approve' ? 'approved' : 'rejected';
      await adminAPI.updateDeposit(selectedDeposit.id, {
        status,
        admin_notes: adminNotes
      });
      toast.success(`Deposit ${status} successfully`);
      setActionModalOpen(false);
      fetchDeposits();
    } catch (error) {
      toast.error(`Failed to ${actionType} deposit`);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      approved: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return styles[status] || styles.pending;
  };

  const getCryptoIcon = (crypto) => {
    const colors = {
      BTC: 'text-orange-400',
      ETH: 'text-blue-400',
      USDT: 'text-emerald-400'
    };
    return colors[crypto] || 'text-gray-400';
  };

  const statCards = [
    { label: 'Total Deposits', value: stats.total || 0, icon: Download, color: 'from-cyan-500 to-blue-500' },
    { label: 'Pending', value: stats.pending || 0, icon: Clock, color: 'from-yellow-500 to-orange-500' },
    { label: 'Approved', value: stats.approved || 0, icon: CheckCircle, color: 'from-emerald-500 to-green-500' },
    { label: 'Rejected', value: stats.rejected || 0, icon: XCircle, color: 'from-red-500 to-pink-500' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Deposit Management</h1>
          <p className="text-gray-400 text-sm mt-1">Review and manage user deposit requests</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-emerald-400">${(stats.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
          <p className="text-sm text-gray-400">Total Approved</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-4"
          >
            <div className={`w-10 h-10 rounded-lg bg-gradient-to-r ${stat.color} flex items-center justify-center mb-3`}>
              <stat.icon className="w-5 h-5 text-white" />
            </div>
            <p className="text-2xl font-bold text-white">{stat.value}</p>
            <p className="text-xs text-gray-400">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 sm:flex-none sm:w-48">
          <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full pl-10 pr-8 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white appearance-none cursor-pointer focus:outline-none focus:border-cyan-500 transition-colors"
            data-testid="status-filter"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div className="relative flex-1 sm:flex-none sm:w-48">
          <Bitcoin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <select
            value={cryptoFilter}
            onChange={(e) => setCryptoFilter(e.target.value)}
            className="w-full pl-10 pr-8 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white appearance-none cursor-pointer focus:outline-none focus:border-cyan-500 transition-colors"
            data-testid="crypto-filter"
          >
            <option value="all">All Crypto</option>
            <option value="BTC">Bitcoin (BTC)</option>
            <option value="ETH">Ethereum (ETH)</option>
            <option value="USDT">Tether (USDT)</option>
          </select>
        </div>
      </div>

      {/* Deposits Table - Desktop */}
      <div className="hidden lg:block bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900/50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">User</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Crypto</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Transaction Hash</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Proof</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Date</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <Loader2 className="w-8 h-8 text-cyan-500 animate-spin mx-auto" />
                    <p className="text-gray-400 mt-2">Loading deposits...</p>
                  </td>
                </tr>
              ) : deposits.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <Download className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">No deposits found</p>
                  </td>
                </tr>
              ) : (
                deposits.map((deposit) => (
                  <tr key={deposit.id} className="hover:bg-gray-700/30 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-white font-medium">{deposit.user_name || 'Unknown'}</p>
                        <p className="text-gray-400 text-sm">{deposit.user_email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-emerald-400 font-semibold">${(deposit.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`font-medium ${getCryptoIcon(deposit.crypto_type)}`}>
                        {deposit.crypto_type || 'BTC'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {deposit.transaction_hash ? (
                        <span className="text-gray-300 font-mono text-xs">
                          {deposit.transaction_hash.substring(0, 16)}...
                        </span>
                      ) : (
                        <span className="text-gray-500">Not provided</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {deposit.proof_image ? (
                        <button
                          onClick={() => handleViewProof(deposit)}
                          className="flex items-center gap-1 text-cyan-400 hover:text-cyan-300"
                        >
                          <Image className="w-4 h-4" />
                          <span className="text-sm">View</span>
                        </button>
                      ) : (
                        <span className="text-gray-500 text-sm">None</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadge(deposit.status)}`}>
                        {deposit.status?.toUpperCase() || 'PENDING'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-400 text-sm">
                      {deposit.created_at ? new Date(deposit.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleViewDetails(deposit)}
                          className="p-2 text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {deposit.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleAction(deposit, 'approve')}
                              className="p-2 text-gray-400 hover:text-emerald-400 hover:bg-emerald-500/10 rounded-lg transition-colors"
                              title="Approve"
                              data-testid={`approve-deposit-${deposit.id}`}
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleAction(deposit, 'reject')}
                              className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                              title="Reject"
                              data-testid={`reject-deposit-${deposit.id}`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Deposits Cards - Mobile/Tablet */}
      <div className="lg:hidden space-y-4">
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 text-cyan-500 animate-spin mx-auto" />
            <p className="text-gray-400 mt-2">Loading deposits...</p>
          </div>
        ) : deposits.length === 0 ? (
          <div className="text-center py-12">
            <Download className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No deposits found</p>
          </div>
        ) : (
          deposits.map((deposit) => (
            <motion.div
              key={deposit.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-4"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-white font-semibold">{deposit.user_name || 'Unknown'}</p>
                  <p className="text-gray-400 text-sm">{deposit.user_email}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadge(deposit.status)}`}>
                  {deposit.status?.toUpperCase() || 'PENDING'}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                <div>
                  <p className="text-gray-500">Amount</p>
                  <p className="text-emerald-400 font-semibold">${(deposit.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                </div>
                <div>
                  <p className="text-gray-500">Crypto</p>
                  <p className={`font-medium ${getCryptoIcon(deposit.crypto_type)}`}>{deposit.crypto_type || 'BTC'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Date</p>
                  <p className="text-gray-300">{deposit.created_at ? new Date(deposit.created_at).toLocaleDateString() : '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Proof</p>
                  {deposit.proof_image ? (
                    <button
                      onClick={() => handleViewProof(deposit)}
                      className="flex items-center gap-1 text-cyan-400 hover:text-cyan-300"
                    >
                      <Image className="w-4 h-4" />
                      <span>View</span>
                    </button>
                  ) : (
                    <span className="text-gray-500">None</span>
                  )}
                </div>
              </div>
              
              {deposit.transaction_hash && (
                <div className="mb-4 p-2 bg-gray-900/50 rounded-lg">
                  <p className="text-gray-500 text-xs mb-1">Transaction Hash</p>
                  <p className="text-gray-300 font-mono text-xs truncate">{deposit.transaction_hash}</p>
                </div>
              )}
              
              <div className="flex gap-2 pt-3 border-t border-gray-700/50">
                <button
                  onClick={() => handleViewDetails(deposit)}
                  className="flex-1 flex items-center justify-center gap-2 py-2 text-sm bg-gray-700/50 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors"
                >
                  <Eye className="w-4 h-4" /> Details
                </button>
                {deposit.status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleAction(deposit, 'approve')}
                      className="flex-1 flex items-center justify-center gap-2 py-2 text-sm bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-lg transition-colors"
                    >
                      <Check className="w-4 h-4" /> Approve
                    </button>
                    <button
                      onClick={() => handleAction(deposit, 'reject')}
                      className="flex-1 flex items-center justify-center gap-2 py-2 text-sm bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4" /> Reject
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* View Details Modal */}
      <AnimatePresence>
        {viewModalOpen && selectedDeposit && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setViewModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-gray-800 border border-gray-700 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-700 flex items-center justify-between sticky top-0 bg-gray-800">
                <h2 className="text-xl font-bold text-white">Deposit Details</h2>
                <button
                  onClick={() => setViewModalOpen(false)}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Status</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadge(selectedDeposit.status)}`}>
                    {selectedDeposit.status?.toUpperCase()}
                  </span>
                </div>
                
                <div className="bg-gradient-to-r from-emerald-500/20 to-green-500/20 border border-emerald-500/30 rounded-xl p-4 text-center">
                  <p className="text-gray-400 text-sm mb-1">Deposit Amount</p>
                  <p className="text-3xl font-bold text-emerald-400">
                    ${(selectedDeposit.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                  <p className={`text-sm mt-1 ${getCryptoIcon(selectedDeposit.crypto_type)}`}>
                    {selectedDeposit.crypto_type || 'BTC'}
                  </p>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-gray-700/50">
                    <span className="text-gray-400">User</span>
                    <span className="text-white">{selectedDeposit.user_name}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-700/50">
                    <span className="text-gray-400">Email</span>
                    <span className="text-cyan-400">{selectedDeposit.user_email}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-700/50">
                    <span className="text-gray-400">Date</span>
                    <span className="text-white">
                      {selectedDeposit.created_at ? new Date(selectedDeposit.created_at).toLocaleString() : '-'}
                    </span>
                  </div>
                  {selectedDeposit.transaction_hash && (
                    <div className="py-2 border-b border-gray-700/50">
                      <p className="text-gray-400 mb-1">Transaction Hash</p>
                      <p className="text-white font-mono text-sm break-all">{selectedDeposit.transaction_hash}</p>
                    </div>
                  )}
                  {selectedDeposit.note && (
                    <div className="py-2 border-b border-gray-700/50">
                      <p className="text-gray-400 mb-1">User Note</p>
                      <p className="text-white">{selectedDeposit.note}</p>
                    </div>
                  )}
                  {selectedDeposit.admin_notes && (
                    <div className="py-2">
                      <p className="text-gray-400 mb-1">Admin Notes</p>
                      <p className="text-white">{selectedDeposit.admin_notes}</p>
                    </div>
                  )}
                </div>
                
                {selectedDeposit.proof_image && (
                  <button
                    onClick={() => {
                      setViewModalOpen(false);
                      handleViewProof(selectedDeposit);
                    }}
                    className="w-full py-3 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/30 text-cyan-400 rounded-xl transition-colors flex items-center justify-center gap-2"
                  >
                    <Image className="w-5 h-5" />
                    View Proof Image
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Proof Image Modal */}
      <AnimatePresence>
        {proofModalOpen && selectedDeposit && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setProofModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative max-w-3xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setProofModalOpen(false)}
                className="absolute -top-12 right-0 p-2 text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
              
              <div className="bg-gray-800 border border-gray-700 rounded-2xl overflow-hidden">
                <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                  <h3 className="text-white font-semibold">Proof of Payment</h3>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${selectedDeposit.proof_image}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 text-sm"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open Full Size
                  </a>
                </div>
                <div className="p-4">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${selectedDeposit.proof_image}`}
                    alt="Proof of payment"
                    className="w-full rounded-lg"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect fill="%231f2937" width="400" height="300"/><text fill="%239ca3af" x="50%" y="50%" dominant-baseline="middle" text-anchor="middle">Image not available</text></svg>';
                    }}
                  />
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Approve/Reject Action Modal */}
      <AnimatePresence>
        {actionModalOpen && selectedDeposit && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setActionModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-gray-800 border border-gray-700 rounded-2xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 text-center">
                <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${
                  actionType === 'approve' ? 'bg-emerald-500/20' : 'bg-red-500/20'
                }`}>
                  {actionType === 'approve' ? (
                    <Check className="w-8 h-8 text-emerald-400" />
                  ) : (
                    <X className="w-8 h-8 text-red-400" />
                  )}
                </div>
                <h2 className="text-xl font-bold text-white mb-2">
                  {actionType === 'approve' ? 'Approve' : 'Reject'} Deposit?
                </h2>
                <p className="text-gray-400">
                  {actionType === 'approve' 
                    ? `This will add $${selectedDeposit.amount?.toLocaleString()} to ${selectedDeposit.user_name}'s balance.`
                    : `This will reject the deposit request from ${selectedDeposit.user_name}.`
                  }
                </p>
              </div>
              
              <div className="px-6 pb-6">
                <label className="block text-sm text-gray-400 mb-2">Admin Notes (optional)</label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder={actionType === 'approve' ? 'Add any notes...' : 'Reason for rejection...'}
                  rows={3}
                  className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white resize-none focus:outline-none focus:border-cyan-500"
                  data-testid="admin-notes-input"
                />
              </div>
              
              <div className="p-6 border-t border-gray-700 flex gap-3">
                <button
                  onClick={() => setActionModalOpen(false)}
                  className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={executeAction}
                  disabled={actionLoading}
                  className={`flex-1 py-3 rounded-xl transition-all flex items-center justify-center gap-2 text-white ${
                    actionType === 'approve'
                      ? 'bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600'
                      : 'bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600'
                  }`}
                  data-testid="confirm-action-btn"
                >
                  {actionLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : actionType === 'approve' ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <X className="w-5 h-5" />
                  )}
                  {actionType === 'approve' ? 'Approve Deposit' : 'Reject Deposit'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
